import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import { ArrowLeft } from "@phosphor-icons/react";

/**
 * Public legal page — fetches the current Terms of Use + Privacy Policy
 * from the backend and renders the markdown as styled HTML.
 *
 * Linked from:
 *  - Cadastro (`/cadastro`) — checkbox label
 *  - Footer da landing/app
 *  - Email transacional pós-cadastro
 *
 * Stays accessible without authentication so prospects can read before
 * signing up, and so links shared with clients remain valid forever.
 */
export default function LegalTerms() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/legal/terms/current")
      .then((r) => { if (alive) { setData(r.data); setLoading(false); } })
      .catch((e) => {
        if (alive) {
          setError(e?.response?.data?.detail || "Não foi possível carregar os termos.");
          setLoading(false);
        }
      });
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="legal-terms-page">
      <header className="border-b border-slate-200/70 sticky top-0 bg-white/80 backdrop-blur-lg z-10">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link
            to="/cadastro"
            className="inline-flex items-center gap-1.5 text-[12px] text-slate-600 hover:text-slate-900 transition-colors"
            data-testid="legal-back-link"
          >
            <ArrowLeft size={12} weight="bold" />
            Voltar
          </Link>
          {data && (
            <span className="text-[10.5px] text-slate-400 tabular-nums tracking-[0.06em]" data-testid="legal-version-badge">
              v{data.version}
            </span>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        {loading && (
          <div className="text-slate-500 text-sm" data-testid="legal-loading">
            Carregando documento…
          </div>
        )}
        {error && (
          <div className="rounded-xl border border-rose-200/60 bg-rose-50/50 px-4 py-3 text-[13px] text-rose-800" data-testid="legal-error">
            {error}
          </div>
        )}
        {data && (
          <article
            className="legal-content"
            data-testid="legal-content"
            dangerouslySetInnerHTML={{
              // P1-5 audit fix — sanitize before injecting. Content is
              // controlled by admin today, but DOMPurify gives us defense
              // in depth if it ever becomes user-editable.
              __html: DOMPurify.sanitize(
                renderMarkdown(data.body_markdown),
                { USE_PROFILES: { html: true } },
              ),
            }}
          />
        )}
      </main>

      <footer className="border-t border-slate-200/70 mt-12">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-6 text-[11px] text-slate-500 leading-relaxed">
          NOVEN PARTICIPACOES LTDA · CNPJ 65.858.327/0001-44 · SHS Quadra 6, Conjunto A, Bloco A, Sala 501 · Brasília/DF · CEP 70.316-102
        </div>
      </footer>

      <style>{`
        .legal-content { font-size: 14px; line-height: 1.7; color: #1E293B; }
        .legal-content h1 { font-size: 28px; font-weight: 700; letter-spacing: -0.02em; color: #0F172A; margin-top: 0; margin-bottom: 24px; }
        .legal-content h2 { font-size: 18px; font-weight: 700; letter-spacing: -0.015em; color: #0F172A; margin-top: 36px; margin-bottom: 14px; padding-top: 8px; }
        .legal-content h3 { font-size: 15px; font-weight: 600; color: #0F172A; margin-top: 24px; margin-bottom: 10px; }
        .legal-content p { margin: 12px 0; }
        .legal-content ul, .legal-content ol { margin: 12px 0; padding-left: 22px; }
        .legal-content li { margin: 6px 0; }
        .legal-content strong { font-weight: 600; color: #0F172A; }
        .legal-content blockquote {
          border-left: 3px solid #0F172A;
          padding: 10px 16px;
          margin: 16px 0;
          background: #F8FAFC;
          border-radius: 6px;
          font-style: italic;
          color: #334155;
        }
        .legal-content hr { border: 0; border-top: 1px solid #E2E8F0; margin: 32px 0; }
        .legal-content a { color: #0F172A; text-decoration: underline; text-underline-offset: 3px; text-decoration-color: #94A3B8; }
        .legal-content a:hover { text-decoration-color: #0F172A; }
        .legal-content code { background: #F1F5F9; padding: 1px 6px; border-radius: 4px; font-size: 12.5px; }
      `}</style>
    </div>
  );
}

// Minimal, dependency-free markdown → HTML renderer. We do NOT pull in
// `marked` or `react-markdown` to keep the bundle small and the security
// surface tiny — the markdown is server-controlled (versioned files
// under /app/backend/legal/), so we trust the input.
function renderMarkdown(md) {
  let html = md;
  // Escape angle brackets first so user-content doesn't open elements.
  html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  // Headings
  html = html.replace(/^###### (.+)$/gm, "<h6>$1</h6>");
  html = html.replace(/^##### (.+)$/gm, "<h5>$1</h5>");
  html = html.replace(/^#### (.+)$/gm, "<h4>$1</h4>");
  html = html.replace(/^### (.+)$/gm, "<h3>$1</h3>");
  html = html.replace(/^## (.+)$/gm, "<h2>$1</h2>");
  html = html.replace(/^# (.+)$/gm, "<h1>$1</h1>");
  // Horizontal rules
  html = html.replace(/^---$/gm, "<hr/>");
  // Bold + italic
  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  // Links [text](url)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, t, u) =>
    `<a href="${u}" target="_blank" rel="noopener noreferrer">${t}</a>`,
  );
  // Blockquotes
  html = html.replace(/^&gt; (.+)$/gm, "<blockquote>$1</blockquote>");
  // Unordered lists
  html = html.replace(/(?:^- (.+)\n?)+/gm, (block) => {
    const items = block.trim().split("\n").map((l) => l.replace(/^- /, "")).map((l) => `<li>${l}</li>`).join("");
    return `<ul>${items}</ul>`;
  });
  // Paragraphs — wrap loose text that isn't already a block.
  html = html
    .split(/\n{2,}/)
    .map((chunk) => {
      const t = chunk.trim();
      if (!t) return "";
      if (/^<(h\d|ul|ol|blockquote|hr|p|table)/i.test(t)) return t;
      return `<p>${t.replace(/\n/g, "<br/>")}</p>`;
    })
    .join("\n");
  return html;
}
